import { TutorialStep } from '../types'
import { selectors } from '../selectors'
import { createTabAutoClickHandler } from '../stepManagement/actionHelpers'
import { waitForElementAndScroll } from '../stepManagement/elementUtils'

export const patternBuilderSteps: readonly TutorialStep[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to Pattern Builder',
    content:
      'Patterns contain groups of rules that are used to identify trade entries. You can backtest patterns to see historical performance or create alerts to get real-time notifications when your pattern’s rules are met.',
    section: 'Welcome',
    disableBeacon: true,
    isWelcomeScreen: true,
    video: 'patternBuilderIntro',
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.tradingHousePatternsSection),
    target: selectors.tradingHousePatternsSection,
    placement: 'bottom',
    title: 'Trade Center Patterns',
    content:
      'These patterns are provided by the company to help you get started. Many of these patterns are used by our coaches in our weekly live trading webinars.',
    section: 'Trade Center Patterns',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.yourPatternsSection),
    target: selectors.yourPatternsSection,
    placement: 'bottom',
    title: 'Your Patterns',
    content:
      'These are patterns you copy from the company (above) or the Showcase, and patterns you author yourself.',
    section: 'Your Patterns',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.yourPatternsSection),
    target: selectors.patternItem,
    placement: 'bottom',
    title: 'Pattern Settings',
    content: 'The following settings can be used with a pattern.',
    section: 'Pattern Settings',
    disableBeacon: true,
    floaterProps: {
      options: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 40,
            },
          },
          {
            name: 'flip',
            options: {},
          },
        ],
      },
    },
  },
  {
    target: selectors.patternName,
    placement: 'right',
    title: 'Pattern Name',
    content: 'Enter a name that describes the pattern so you can easily identify it later.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.patternBuyTab,
    placement: 'right',
    title: 'Trigger (Buy/Sell)',
    content:
      'You will set up your buy conditions first, then you’ll set up your sell conditions later under the Sell tab. Note that by default you are on the Buy settings tab.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.triggerChartSelector,
    placement: 'right',
    title: 'Trigger Interval',
    content:
      'Click here to adjust the Periodicity and Timeframe to the desired chart timeframe you are using to trigger this pattern. This is normally the lowest timeframe chart used in your pattern rules or conditions. Use this area to choose when you would like a Pattern Alert to notify you (via email, SMS, and in app) when all conditions are met within the pattern.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.userPatternGroup,
    placement: 'left',
    title: 'Groups',
    content:
      'Groups allow you to combine multiple rules and even other groups. Start by naming your group and choosing a chart timeframe.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.addRuleButton,
    placement: 'right',
    title: 'Add Rules',
    content: 'Add rules to groups.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.patternCondition,
    placement: 'top',
    title: 'Rules',
    content: 'Rules are made of indicators and conditions that determine when a pattern triggers.',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.conditionSelector,
    placement: 'top',
    title: 'Setup Rules',
    content:
      'Start by choosing an indicator that you wish to add to your group, then choose the condition that you want to apply, then choose an indicator or value to satisfy the condition',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.patternConditionDragHandle,
    placement: 'right',
    title: 'Sorting Conditions',
    content: 'Reorder rules by grabbing here and dragging to another location within any group',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    target: selectors.savePatternButton,
    placement: 'right',
    title: 'Save Pattern',
    content:
      'Click here to save your pattern. Each time you save a new version is created (see Versions tab under View Details).',
    section: 'Pattern Settings',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.patternConditionButton),
    target: selectors.patternConditionButton,
    placement: 'right',
    title: 'Pattern Details',
    content: 'Use this panel to view more details about your patterns.',
    section: 'Pattern Details',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.yourPatternsSection),
    target: selectors.patternAlertsTab,
    placement: 'right',
    title: 'Alerts',
    content: 'View alerts that are using this pattern',
    section: 'Pattern Details',
    onEnter: createTabAutoClickHandler(selectors.patternAlertsTab),
    disableBeacon: true,
  },
  {
    target: selectors.patternBacktestsTab,
    placement: 'right',
    title: 'Backtests',
    content: 'View backtests that have been run using this pattern',
    section: 'Pattern Details',
    onEnter: createTabAutoClickHandler(selectors.patternBacktestsTab),
    disableBeacon: true,
  },
  {
    target: selectors.patternVersionTab,
    placement: 'left',
    title: 'Versions',
    content: 'View all versions of your pattern.',
    section: 'Pattern Details',
    onEnter: createTabAutoClickHandler(selectors.patternVersionTab),
    disableBeacon: true,
  },
  {
    target: selectors.patternActionsSection,
    placement: 'top',
    title: 'Actions',
    content: 'Create a new Alert or run a new Backtest for this pattern.',
    section: 'Pattern Details',
    disableBeacon: true,
  },
  {
    target: selectors.createPatternButton,
    placement: 'left',
    title: 'Create Patterns',
    content: 'Click here to create a new pattern',
    section: 'New Patterns',
    disableBeacon: true,
  },
  {
    beforeShowPromise: () => waitForElementAndScroll(selectors.patternCopyButton),
    target: selectors.patternCopyButton,
    placement: 'left',
    title: 'Copy Patterns',
    content: 'Click here to copy this pattern to Your Patterns',
    section: 'New Patterns',
    disableBeacon: true,
  },
  {
    target: 'body',
    placement: 'center',
    title: 'Rate',
    content:
      'Please rate this tutorial (1 - poor, 5 - exceptional). Please provide us details of your experience or suggestions on improving this tutorial.',
    isFeedback: true,
    hideFooter: true,
    disableBeacon: true,
    section: 'Review',
  },
]
